import {
    Component,
    inject,
    Injectable,
    OnDestroy,
    OnInit
} from '@angular/core';

import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogRef
} from '@angular/material/dialog';

import { TranslateModule } from '@ngx-translate/core';
import { MatProgressBarModule } from '@angular/material/progress-bar';

import {
    BaseDialogService,
    NonNullableConfigData
} from '@tilby/tilby-ui-lib/components/tilby-dialog';

import {
    lastValueFrom,
    Subject
} from 'rxjs';

type BaseAction = {
    label: string;
    disabled?: boolean;
}

export namespace WaitDialogModel {
    export type StatusSubject = Subject<Partial<WaitDialogState>>;

    export type WaitDialogState = {
        message: string;
        secondaryMessage?: string;
        cancelAction?: BaseAction;
        customActions?: (BaseAction & { callback(): any })[];
        showProgress?: boolean;
    }

    export type InputData = WaitDialogState & {
        stateSubject?: StatusSubject;
        timeout?: {
            target: any;
            // process;
        };
        promise?: Promise<any>;
    }

    export type State = {
        message: string;
        secondaryMessage?: string;
        cancelAction?: BaseAction;
        customActions?: (BaseAction & { callback(): any })[];
        showProgress?: boolean;
    }
}


type DialogResult<T> = 
    | { status: 'success'; data: T }
    | { status: 'error'; error: any }
    | { status: 'canceled' };

@Component({
    imports: [
        TranslateModule,
        MatProgressBarModule
    ],
    selector: 'app-wait-dialog',
    standalone: true,
    templateUrl: './wait-dialog.component.html',
    styleUrls: ['./wait-dialog.component.scss'],
    host: { class: 'tw-min-w-fit tw-flex tw-justify-center tw-items-center tw-h-full tw-flex-col tw-px-6 tw-py-6' }
})
class WaitDialogComponent implements OnInit, OnDestroy {
    public data: WaitDialogModel.InputData = { ...inject(MAT_DIALOG_DATA) };
    private readonly dialogRef = inject(MatDialogRef);

    timeoutProgress = 0;
    timeoutTarget = 0;
    intervalId?: NodeJS.Timer;

    ngOnInit() {
        if (this.data.promise) {
            this.data.promise.then((result) => {
                this.dialogRef.close({ status: 'success', data: result });
            }, (error) => {
                this.dialogRef.close({ status: 'error', error });
            });
        }

        if (this.data.timeout) {
            this.timeoutTarget = (this.data.timeout.target) * 1000;

            const progressUpdate = () => {
                this.timeoutProgress += 100;

                if (this.timeoutProgress >= this.timeoutTarget) {
                    clearInterval(this.intervalId);
                    this.dialogRef.close({ status: 'success' });
                }
            };

            this.intervalId = setInterval(progressUpdate, 100);
        }

        if (this.data.stateSubject) {
            this.data.stateSubject.subscribe((state) => {
                this.updateState(state);
            });
        }
    }

    ngOnDestroy(): void {
        if (this.intervalId) {
            clearInterval(this.intervalId);
        }

        if (this.data.stateSubject) {
            this.data.stateSubject.complete();
        }
    }

    updateState(state: Partial<WaitDialogModel.InputData>) {
        this.data = {
            ...this.data,
            ...state 
        };

        if(state.cancelAction) {
            delete this.data.customActions;
        }

        if(state.customActions) {
            delete this.data.cancelAction;
        }
    };
}

@Injectable({
    providedIn: 'root'
})
export class WaitDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);

    public openDialog<T = any>(options: NonNullableConfigData<WaitDialogModel.InputData>): Promise<T> {
        const config: NonNullableConfigData<WaitDialogModel.InputData> = {
            ...this.switchMobileDesktopDimensions({ width: '800px' }),
            disableClose: true,
            data: options.data
        };

        return lastValueFrom(
            this.dialogRef.open<WaitDialogComponent, WaitDialogModel.InputData, DialogResult<T>>(WaitDialogComponent, config).afterClosed()
        ).then((res) => {
            if (!res || res.status === 'canceled') {
                return Promise.reject('CANCELED');
            }

            if (res.status === 'error') {
                return Promise.reject(res.error);
            }

            return res.data;
        });
    }
}